import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import {
  UserPlus,
  RefreshCw,
  ShieldOff,
  ArrowRight,
  Users,
  Clock,
  CheckCircle2,
  Lock,
  // TrendingUp,
  AlertCircle,
  // Activity,
} from 'lucide-react';
import { DashboardApiResponse, TourRegistrationDashboard } from '../../services/newRegistrationService';
import { useEffect, useState } from 'react';
import { FullScreenLoading } from '../../components/ui/loader';
import { useScreenPermission } from "hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

interface TORegistrationManagementDashboardProps {
  onNavigate: (page: string) => void;
}

export default function TORegistrationManagementDashboard({ onNavigate }: TORegistrationManagementDashboardProps) {
  const { setPageHeader } = usePageHeader();
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);
  // const [apiErrors, setApiErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { screenName, icon: ScreenIcon } = useScreenPermission();

  // Mock data - In production, this would come from API
  const stats = {
    registrationRequests: {
      total: dashboardData?.total || 0,
      pending: dashboardData?.submitted || 0,
      approved: dashboardData?.approved || 0,
      rejected: 5,
      trending: 'up',
      percentChange: '+15%'
    },
    renewalRequests: {
      total: 23,
      pending: 8,
      approved: 15,
      dueSoon: 18,
      trending: 'up',
      percentChange: '+8%'
    },
    blockedUsers: {
      total: 7,
      active: 5,
      expired: 2,
      tourOperators: 3,
      partners: 4,
      trending: 'down',
      percentChange: '-12%'
    }
  };

  const getCardGradient = (color: string) => {
    switch (color) {
      case 'blue':
        return 'from-white to-sky-50 border-sky-200';
      case 'green':
        return 'from-white to-emerald-50 border-emerald-200';
      case 'red':
        return 'from-white to-red-50 border-red-200';
      default:
        return 'from-white to-gray-50 border-gray-200';
    }
  };

  // const getTrendIcon = (trend: string) => {
  //   if (trend === 'up') {
  //     return <TrendingUp className="w-4 h-4 text-green-600" />;
  //   }
  //   return <Activity className="w-4 h-4 text-orange-600" />;
  // };


  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      const result = await TourRegistrationDashboard();
      if (result) {
        setDashboardData(result);
      }
      //  else {
      //   setApiErrors(result);
      // }
      setLoading(false);
    };
    fetchDashboard();
  }, []);


  return (
    <>
      {
        loading && <FullScreenLoading />
      }
      <div className="space-y-6"> 
        {/* Header */}
        {/* Main Summary Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Card 1: Registration Request */}
          <Card
            className={`group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden bg-gradient-to-br ${getCardGradient('blue')} border-2 hover:border-sky-300`}
            // onClick={() => onNavigate('to-registration-list')}
            style={{ marginTop: "0px" }}
            onClick={() => onNavigate('registration/list')}
          >
            <CardContent className="p-6">
              {/* Icon Header with Badge */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform"
                  style={{
                    backgroundColor: '#5B9BD5',
                    boxShadow: '0 8px 16px rgba(91, 155, 213, 0.3)'
                  }}
                >
                  <UserPlus className="w-8 h-8 text-white" />
                </div>
                {/* <div className="flex items-center gap-2">
                {getTrendIcon(stats.registrationRequests.trending)}
                <span className="text-sm text-gray-600">{stats.registrationRequests.percentChange}</span>
              </div> */}
              </div>

              {/* Title and Description */}
              <h2 className="text-2xl mb-2" style={{ color: '#5B9BD5' }}>
                Registration Requests
              </h2>
              <p className="text-gray-600 text-sm mb-5">
                Manage and review new tour operator registration applications
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-sky-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <div className="text-xs text-gray-600">Pending</div>
                  </div>
                  <div className="text-2xl text-orange-600">{stats.registrationRequests.pending || dashboardData?.submitted}</div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-sky-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <div className="text-xs text-gray-600">Approved</div>
                  </div>
                  <div className="text-2xl text-green-600">{stats.registrationRequests.approved || dashboardData?.approved}</div>
                </div>
              </div>

              {/* Quick Info Row */}
              <div className="bg-sky-100 rounded-lg p-3 mb-5">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#5B9BD5' }} />
                  <div>
                    <div className="text-xs font-semibold text-gray-900 mb-0.5">Action Required</div>
                    <div className="text-xs text-gray-700">
                      {stats.registrationRequests.pending || dashboardData?.submitted} applications awaiting your review
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Count */}
              {/* <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Total Registrations</span>
                <span className="text-2xl text-gray-900">{stats.registrationRequests.total || dashboardData?.total}</span>
              </div> */}

              {/* Action Button */}
              <Button
                className="w-full text-white shadow-lg hover:shadow-xl transition-all group"
                style={{ backgroundColor: '#5B9BD5' }}
                // onClick={() => onNavigate('/to-registrationAdmin')}
                onClick={() => onNavigate('registration/list')}
              >
                <span>View All Registrations</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Card 2: Renewal Request */}
          <Card
            className={`hidden group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden bg-gradient-to-br ${getCardGradient('green')} border-2 hover:border-emerald-300`}
            onClick={() => onNavigate('/registration/to-renewal-list')}
          >
            <CardContent className="p-6">
              {/* Icon Header with Badge */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform"
                  style={{
                    backgroundColor: '#10b981',
                    boxShadow: '0 8px 16px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  <RefreshCw className="w-8 h-8 text-white" />
                </div>
                {/* <div className="flex items-center gap-2">
                {getTrendIcon(stats.renewalRequests.trending)}
                <span className="text-sm text-gray-600">{stats.renewalRequests.percentChange}</span>
              </div> */}
              </div>

              {/* Title and Description */}
              <h2 className="text-2xl text-emerald-600 mb-2">
                Renewal Requests
              </h2>
              <p className="text-gray-600 text-sm mb-5">
                Process tour operator registration renewals and updates
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-emerald-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-orange-500" />
                    <div className="text-xs text-gray-600">Pending</div>
                  </div>
                  <div className="text-2xl text-orange-600">{stats.renewalRequests.pending}</div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-emerald-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <div className="text-xs text-gray-600">Due Soon</div>
                  </div>
                  <div className="text-2xl text-amber-600">{stats.renewalRequests.dueSoon}</div>
                </div>
              </div>

              {/* Quick Info Row */}
              <div className="bg-emerald-100 rounded-lg p-3 mb-5">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-semibold text-gray-900 mb-0.5">Renewal Notice</div>
                    <div className="text-xs text-gray-700">
                      {stats.renewalRequests.dueSoon} operators approaching renewal deadline
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Count */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Total Renewals</span>
                <span className="text-2xl text-gray-900">{stats.renewalRequests.total}</span>
              </div>

              {/* Action Button */}
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all group"
              >
                <span>View All Renewals</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

          {/* Card 3: Block User */}
          <Card
            className={`hidden group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden bg-gradient-to-br ${getCardGradient('red')} border-2 hover:border-red-300`}
            onClick={() => onNavigate('/registration/block-users')}
          >
            <CardContent className="p-6">
              {/* Icon Header with Badge */}
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform"
                  style={{
                    backgroundColor: '#ef4444',
                    boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)'
                  }}
                >
                  <ShieldOff className="w-8 h-8 text-white" />
                </div>
                {/* <div className="flex items-center gap-2">
                {getTrendIcon(stats.blockedUsers.trending)}
                <span className="text-sm text-gray-600">{stats.blockedUsers.percentChange}</span>
              </div> */}
              </div>

              {/* Title and Description */}
              <h2 className="text-2xl text-red-600 mb-2">
                Blocked Users
              </h2>
              <p className="text-gray-600 text-sm mb-5">
                Manage user access restrictions and blocking schedules
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-red-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-red-500" />
                    <div className="text-xs text-gray-600">Active Blocks</div>
                  </div>
                  <div className="text-2xl text-red-600">{stats.blockedUsers.active}</div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-red-100 shadow-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-sky-500" />
                    <div className="text-xs text-gray-600">TO Blocked</div>
                  </div>
                  <div className="text-2xl text-sky-600">{stats.blockedUsers.tourOperators}</div>
                </div>
              </div>

              {/* Quick Info Row */}
              <div className="bg-red-100 rounded-lg p-3 mb-5">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-semibold text-gray-900 mb-0.5">Security Alert</div>
                    <div className="text-xs text-gray-700">
                      {stats.blockedUsers.active} users currently blocked from system access
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Count */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Total Blocked</span>
                <span className="text-2xl text-gray-900">{stats.blockedUsers.total}</span>
              </div>

              {/* Action Button */}
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate('/registration/block-users');
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all group"
              >
                <span>Manage Block List</span>
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </CardContent>
          </Card>

        </div>
      </div>
    </>
  );
}